import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@spz/ui/components/form";
import { SearchableCheckboxTreeField } from "@spz/ui/components/searchable-checkbox-tree-field";
import { Section } from "@spz/ui/components/section";
import { Textarea } from "@spz/ui/components/textarea";
import { TextField } from "@spz/ui/components/textfield";
import { cn } from "@spz/ui/lib/utils";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import type { RoleFormSchemaValues } from "./schemas/role-form.schema";
import { selectRoleFormViewModel } from "./select-role-form.view-model";

interface RoleFormProps {
  initialValues?: RoleFormSchemaValues;
  roleId?: string;
  onSuccess?: () => void;
  className?: string;
  ref?: Ref<HTMLFormElement>;
}
export const RoleForm = ({
  roleId,
  className,
  onSuccess,
  ref,
}: RoleFormProps) => {
  const { t } = useTranslation();
  const { form, onSubmit, permissions } = selectRoleFormViewModel(
    roleId,
    onSuccess,
  );

  return (
    <Section
      title={roleId ? t("edit_role_title") : t("add_role_title")}
      description={t("role_form_description")}
      className={cn("gap-4 px-6 pb-6", className)}
      darken
    >
      <Form {...form}>
        <form
          ref={ref}
          onSubmit={onSubmit}
          id="role-form"
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    {...field}
                    placeholder={t("role_name")}
                    className="w-full"
                    floatingPlaceholder
                    helperText={t("role_name_helper_text")}
                    error={fieldState.error}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("role_description_placeholder")}
                    className="min-h-10 max-h-13"
                    helperText={t("role_description_helper_text")}
                    error={fieldState.error}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="permissionIds"
            control={form.control}
            render={({ field, fieldState }) => (
              <SearchableCheckboxTreeField
                searchPlaceholder={t("search_permissions_placeholder")}
                label={t("role_permissions_section_title")}
                description={t("role_permissions_section_description")}
                emptyList={t("no_permissions")}
                options={permissions.options}
                loading={permissions.loading}
                value={field.value}
                onChange={({ selectedIds }) => {
                  permissions.updateOptions(selectedIds);
                  field.onChange(permissions.getSelectedIds());
                  field.onBlur();
                }}
                error={fieldState.error}
              />
            )}
          />
        </form>
      </Form>
    </Section>
  );
};

RoleForm.displayName = "RoleForm";
